import React, { ReactElement } from 'react';
import { Switch, Route } from 'react-router-dom';

import AppProviders from 'adapter/in/common/AppProviders';
import { log } from 'adapter/in/common/config/pino';
import { dashboardsPath, linksPath } from 'adapter/in/common/routes';

import { dataId } from 'adapter/in/dashboards/config/gtm';

import getDashboardApiClient from 'adapter/out/client/dashboard/getDashboardApiClient';
import getDashboardUseCase from 'use-case/dashboard/get/getDashboardUseCase';
import { DashboardContainer } from 'adapter/in/dashboards/containers/Dashboard';
import { DashboardLinkContainer } from 'adapter/in/dashboards/containers/DashboardLink';
import { EditDashboardContainer } from 'adapter/in/dashboards/containers/EditDashboard';

import getDashboardsApiClient from 'adapter/out/client/dashboards/getDashboardsApiClient';
import getDashboardsUseCase from 'use-case/dashboards/get/getDashboardsUseCase';
import createDashboardUseCase from 'use-case/dashboard/create/createDashboardUseCase';
import createDashboardApiClient from 'adapter/out/client/dashboard/createDashboardApiClient';
import { DashboardsProvider } from 'adapter/in/dashboards/context/Dashboards';
import DashboardsContainer from 'adapter/in/dashboards/containers/Dashboards';
import { DashboardProvider } from 'adapter/in/dashboards/context/Dashboard';
import { DashboardEditProvider } from 'adapter/in/dashboards/context/EditDashboard';
import {
    updateDashboardNameUseCase,
    updateDashboardAccessUseCase,
    updateDashboardPinUseCase,
    updateDashboardStarUseCase,
    updateDashboardFrameUseCase,
} from 'use-case/dashboard/update/updateDashboardUseCase';
import updateDashboardAccessApiClient from 'adapter/out/client/dashboard/updateDashboardAccessApiClient';
import updateDashboardNameApiClient from 'adapter/out/client/dashboard/updateDashboardNameApiClient';

import deleteDashboardUseCase from 'use-case/dashboard/delete/deleteDashboardUseCase';
import deleteDashboardApiClient from 'adapter/out/client/dashboard/deleteDashboardApiClient';
import readWidgetDataUseCase from 'use-case/widgets/data/read/readWidgetDataUseCase';
import readWidgetDataApiClient from 'adapter/out/client/widgets/data/readWidgetDataApiClient';

import { WidgetsProvider } from 'adapter/in/dashboards/context/Widgets/data';
import readWidgetsUseCase from 'use-case/widgets/read/readWidgetsUseCase';
import readWidgetsApiClient from 'adapter/out/client/widgets/readWidgetsApiClient';
import { LayoutsProvider } from 'adapter/in/dashboards/context/Layouts';
import readLayoutsUseCase from 'use-case/layout/read/readLayoutsUseCase';
import readLayoutsApiClient from 'adapter/out/client/layout/readLayoutsApiClient';
import { WidgetsViewProvider } from 'adapter/in/dashboards/context/Widgets/view';

import { FeaturesProvider } from 'adapter/in/common/context/Features/data';
import readFeaturesApiClient from 'adapter/out/client/features/readFeaturesApiClient';
import readFeaturesUseCase from 'use-case/features/read/readFeaturesUseCase';

import { WidgetMenuViewProvider } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';

import updateLayoutApiClient from 'adapter/out/client/layout/updateLayoutApiClient';
import updateLayoutUseCase from 'use-case/layout/update/updateLayoutUseCase';

import { LinksProvider } from 'adapter/in/dashboards/context/Links';
import createLinkUseCase from 'use-case/link/create/createLinkUseCase';
import createLinkApiClient from 'adapter/out/client/link/createLinkApiClient';
import readLinksUseCase from 'use-case/link/read/readLinksUseCase';
import readLinksApiClient from 'adapter/out/client/link/readLinksApiClient';

import readPermissionsUseCase from 'use-case/permissions/read/readPermissionsUseCase';
import { PermissionsContainer } from 'adapter/in/dashboards/containers/Permissions';
import { PermissionsProvider } from 'adapter/in/dashboards/context/Permissions';
import readPermissionsApiClient from 'adapter/out/client/permissions/readPermissionsApiClient';
import buildBaseUrl from 'adapter/in/common/utils/buildBaseUrl';
import { CurrentWidgetConfigurationProvider } from 'adapter/in/common/context/currentWidgetConfiguration';
import { UserGroupsProvider } from 'adapter/in/dashboards/context/UserGroups';
import readUserGroupsUseCase from 'use-case/userGroups/read/readUserGroupsUseCase';
import readUserGroupsApiClient from 'adapter/out/client/userGroups/readUserGroupsApiClient';
import { MarketAppsProvider } from 'adapter/in/dashboards/context/MarketApps';
import readMarketAppsUseCase from 'use-case/marketApps/read/readMarketAppsUseCase';
import readMarketAppsApiClient from 'adapter/out/client/marketApps/readMarketAppsApiClient';
import { LocationProvider } from 'adapter/in/dashboards/context/Location';
import getLocationUseCase from 'use-case/location/readLocationUseCase';
import getLocationApiClient from 'adapter/out/client/location/readLocationApiClient';
import { enableLinkUseCase } from 'use-case/link/update/updateLinkUseCase';
import enableLinkApiClient from 'adapter/out/client/link/enableLinkApiClient';
import deleteLinkUseCase from 'use-case/link/delete/deleteLinkUseCase';
import deleteLinkApiClient from 'adapter/out/client/link/deleteLinkApiClient';
import readCSVLocationLinksUseCase from 'use-case/link/read/readCSVLocationLinksUseCase';
import readCSVLocationLinksApiClient from 'adapter/out/client/link/readCsvLocationLinksApiClient';
import { FormChangesViewProvider } from 'adapter/in/common/context/formChanges';
import updateDashboardPinApiClient from 'adapter/out/client/dashboard/updateDashboardPinApiClient';
import { WidgetProvider } from 'adapter/in/dashboards/context/Widget/data';
import updateWidgetUseCase from 'use-case/widget/update/updateWidgetUseCase';
import updateWidgetApiClient from 'adapter/out/client/widget/updateWidgetApiClient';
import readWidgetUseCase from 'use-case/widget/read/readWidgetUseCase';
import readWidgetApiClient from 'adapter/out/client/widget/readWidgetApiClient';
import createWidgetUseCase from 'use-case/widgets/create/createWidgetUseCase';
import createWidgetApiClient from 'adapter/out/client/widgets/createWidgetApiClient';
import deleteWidgetUseCase from 'use-case/widgets/delete/deleteWidgetUseCase';
import deleteWidgetApiClient from 'adapter/out/client/widgets/deleteWidgetApiClient';
import duplicateDashboardUseCase from 'use-case/dashboard/duplicate/duplicateDashboardUseCase';
import duplicateDashboardApiClient from 'adapter/out/client/dashboard/duplicateDashboardApiClient';
import updateDashboardStarApiClient from 'adapter/out/client/dashboard/updateDashboardStarApiClient';
import {
    CommonDependenciesContextProvider,
    WidgetDataCriteriaDependenciesContextProvider,
    WidgetDataDependenciesContextProvider,
    getCommonDependencies,
    getWidgetDataCriteriaDependencies,
    getWidgetDataDependencies,
} from 'adapter/in/common/commonDependencies';
import deleteDashboardAnalysisUseCase from 'use-case/analysis/deleteDashboardAnalysis/deleteDashboardAnalysisUseCase';
import deleteDashboardAnalysisApiClient from 'adapter/out/client/analysis/deleteDashboardAnalysisApiClient';
import { LayoutsViewProvider } from 'adapter/in/common/context/Layouts/LayoutsContext';
import updateDashboardFrameApiClient from 'adapter/out/client/dashboard/updateDashboardFrameApiClient';

export type Params = { location?: string };
export const matchLocation = (params: Params) => !params.location || (!!params.location && params.location.startsWith('l'));

const App = (props: {
    port: number;
    apiPrefix: string;
    pathUrl: string;
    fetch: (input: RequestInfo, init?: RequestInit | undefined) => Promise<Response>;
}): ReactElement => {
    const { port, apiPrefix, pathUrl, fetch } = props;
    const baseUrl = buildBaseUrl(window.location.protocol, window.location.hostname, port, apiPrefix, window.location.pathname);

    const commonDependencies = getCommonDependencies(fetch, baseUrl);
    const widgetDataDependencies = getWidgetDataDependencies(fetch, baseUrl);
    const widgetDataCriteriaDependencies = getWidgetDataCriteriaDependencies();
    return (
        <AppProviders dataId={dataId} fetch={fetch} baseUrl={baseUrl} errorLog={log.error}>
            <PermissionsProvider readPermissions={readPermissionsUseCase(readPermissionsApiClient(fetch, baseUrl))}>
                <PermissionsContainer>
                    <Switch>
                        <DashboardProvider
                            getDashboard={getDashboardUseCase(getDashboardApiClient(fetch, baseUrl))}
                            updateDashboardName={updateDashboardNameUseCase(updateDashboardNameApiClient(fetch, baseUrl))}
                            updateDashboardAccess={updateDashboardAccessUseCase(updateDashboardAccessApiClient(fetch, baseUrl))}
                            updateDashboardPin={updateDashboardPinUseCase(updateDashboardPinApiClient(fetch, baseUrl))}
                            updateDashboardFrame={updateDashboardFrameUseCase(updateDashboardFrameApiClient(fetch, baseUrl))}
                            deleteDashboard={deleteDashboardUseCase(deleteDashboardApiClient(fetch, baseUrl))}
                            duplicateDashboard={duplicateDashboardUseCase(duplicateDashboardApiClient(fetch, baseUrl))}
                            updateDashboardStar={updateDashboardStarUseCase(updateDashboardStarApiClient(fetch, baseUrl))}
                        >
                            <>
                                <UserGroupsProvider readUserGroups={readUserGroupsUseCase(readUserGroupsApiClient(fetch, baseUrl, 500))}>
                                    <LinksProvider
                                        enableLink={enableLinkUseCase(enableLinkApiClient(fetch, baseUrl))}
                                        deleteLink={deleteLinkUseCase(deleteLinkApiClient(fetch, baseUrl))}
                                        readCSVLocationLinks={readCSVLocationLinksUseCase(readCSVLocationLinksApiClient(fetch, baseUrl))}
                                        createLink={createLinkUseCase(createLinkApiClient(fetch, baseUrl))}
                                        readLinks={readLinksUseCase(readLinksApiClient(fetch, baseUrl))}
                                    >
                                        <WidgetsProvider readWidgets={readWidgetsUseCase(readWidgetsApiClient(fetch, baseUrl))}>
                                            <WidgetsViewProvider>
                                                <LayoutsProvider
                                                    readLayouts={readLayoutsUseCase(readLayoutsApiClient(fetch, baseUrl))}
                                                    updateLayout={updateLayoutUseCase(updateLayoutApiClient(fetch, baseUrl))}
                                                >
                                                    <WidgetProvider
                                                        updateWidget={updateWidgetUseCase(updateWidgetApiClient(fetch, baseUrl))}
                                                        readWidget={readWidgetUseCase(readWidgetApiClient(fetch, baseUrl))}
                                                        readWidgetData={readWidgetDataUseCase(readWidgetDataApiClient(fetch, baseUrl))}
                                                        createWidget={createWidgetUseCase(createWidgetApiClient(fetch, baseUrl))}
                                                        deleteWidget={deleteWidgetUseCase(deleteWidgetApiClient(fetch, baseUrl))}
                                                        deleteDashboardAnalysis={deleteDashboardAnalysisUseCase(
                                                            deleteDashboardAnalysisApiClient(fetch, baseUrl)
                                                        )}
                                                    >
                                                        <CommonDependenciesContextProvider dependencies={commonDependencies}>
                                                            <WidgetDataDependenciesContextProvider dependencies={widgetDataDependencies}>
                                                                <WidgetDataCriteriaDependenciesContextProvider
                                                                    dependencies={widgetDataCriteriaDependencies}
                                                                >
                                                                    <>
                                                                        <FeaturesProvider
                                                                            readFeatures={readFeaturesUseCase(
                                                                                readFeaturesApiClient(fetch, baseUrl)
                                                                            )}
                                                                        >
                                                                            <CurrentWidgetConfigurationProvider>
                                                                                <WidgetMenuViewProvider>
                                                                                    <FormChangesViewProvider>
                                                                                        <DashboardEditProvider>
                                                                                            <LayoutsViewProvider>
                                                                                                <Route
                                                                                                    exact
                                                                                                    path={[
                                                                                                        `/${pathUrl}${dashboardsPath}/:id/edit`,
                                                                                                        `/:location/${pathUrl}${dashboardsPath}/:id/edit`,
                                                                                                    ]}
                                                                                                    render={({ match }) =>
                                                                                                        match.params.id &&
                                                                                                        matchLocation(
                                                                                                            match.params as Params
                                                                                                        ) && (
                                                                                                            <EditDashboardContainer
                                                                                                                id={match.params.id}
                                                                                                            />
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            </LayoutsViewProvider>
                                                                                        </DashboardEditProvider>
                                                                                    </FormChangesViewProvider>
                                                                                </WidgetMenuViewProvider>
                                                                            </CurrentWidgetConfigurationProvider>
                                                                        </FeaturesProvider>

                                                                        <Route
                                                                            exact
                                                                            path={[
                                                                                `/${pathUrl}${dashboardsPath}/:id`,
                                                                                `/:location/${pathUrl}${dashboardsPath}/:id`,
                                                                            ]}
                                                                            render={({ match }) =>
                                                                                match.params.id &&
                                                                                matchLocation(match.params as Params) && (
                                                                                    <DashboardContainer id={match.params.id} />
                                                                                )
                                                                            }
                                                                        />

                                                                        <Route
                                                                            exact
                                                                            // Include OR for new format
                                                                            path={`/${linksPath}/:linkId/${dashboardsPath}/:id`}
                                                                            render={({ match }) =>
                                                                                match.params.id && (
                                                                                    <DashboardLinkContainer dashboardId={match.params.id} />
                                                                                )
                                                                            }
                                                                        />
                                                                    </>
                                                                </WidgetDataCriteriaDependenciesContextProvider>
                                                            </WidgetDataDependenciesContextProvider>
                                                        </CommonDependenciesContextProvider>
                                                    </WidgetProvider>
                                                </LayoutsProvider>
                                            </WidgetsViewProvider>
                                        </WidgetsProvider>
                                    </LinksProvider>
                                </UserGroupsProvider>
                                <DashboardsProvider
                                    getDashboards={getDashboardsUseCase(getDashboardsApiClient(fetch, baseUrl))}
                                    createDashboard={createDashboardUseCase(createDashboardApiClient(fetch, baseUrl))}
                                >
                                    <LocationProvider getLocation={getLocationUseCase(getLocationApiClient(fetch, baseUrl))}>
                                        <UserGroupsProvider
                                            readUserGroups={readUserGroupsUseCase(readUserGroupsApiClient(fetch, baseUrl, 500))}
                                        >
                                            <LinksProvider
                                                enableLink={enableLinkUseCase(enableLinkApiClient(fetch, baseUrl))}
                                                deleteLink={deleteLinkUseCase(deleteLinkApiClient(fetch, baseUrl))}
                                                readCSVLocationLinks={readCSVLocationLinksUseCase(
                                                    readCSVLocationLinksApiClient(fetch, baseUrl)
                                                )}
                                                createLink={createLinkUseCase(createLinkApiClient(fetch, baseUrl))}
                                                readLinks={readLinksUseCase(readLinksApiClient(fetch, baseUrl))}
                                            >
                                                <MarketAppsProvider
                                                    readMarketApps={readMarketAppsUseCase(readMarketAppsApiClient(fetch, baseUrl))}
                                                >
                                                    <Route
                                                        exact
                                                        path={[
                                                            `/${pathUrl}`,
                                                            `/${pathUrl}${dashboardsPath}`,
                                                            `/:location/${pathUrl}`,
                                                            `/:location/${pathUrl}${dashboardsPath}`,
                                                        ]}
                                                        render={({ match }) =>
                                                            matchLocation(match.params as Params) && (
                                                                <DashboardsContainer pathUrl={pathUrl} />
                                                            )
                                                        }
                                                    />
                                                </MarketAppsProvider>
                                            </LinksProvider>
                                        </UserGroupsProvider>
                                    </LocationProvider>
                                </DashboardsProvider>
                            </>
                        </DashboardProvider>
                    </Switch>
                </PermissionsContainer>
            </PermissionsProvider>
        </AppProviders>
    );
};

export default App;
